import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
  ControlContainer,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';
import {
  AvailableBooleanOperators,
  AvailableDateOperators,
  AvailableNoneNullableNumericOperators,
  AvailableNumericOperators,
  AvailableStringOperators,
} from '@em/data-feed/util-types';
import {OperatorType} from '@em/shared/mapping-engine/domain-models';
import {EmSelectOperandModule} from '@em/shared/mapping-engine/feature';
import {EmSelectModule} from '@em/shared/ui';
import {TranslateModule} from '@ngx-translate/core';
import {startWith} from 'rxjs/operators';

@Component({
  selector: 'em-product-group-condition',
  templateUrl: './product-group-condition.component.html',
  styleUrls: ['./product-group-condition.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    EmSelectModule,
    EmSelectOperandModule,
  ],
})
export class EmProductGroupConditionComponent implements OnInit {
  private readonly _controlContainer = inject(ControlContainer);
  private readonly _destroyRef = inject(DestroyRef);

  operatorType = input.required<OperatorType | null | undefined>();
  isNullable = input.required<boolean | undefined>();
  conditionForm: UntypedFormGroup | null | undefined;
  selectedOperatorName = signal<string | null>(null);
  selectedOperator = computed(() =>
    this.availableOperators()?.find(
      (op) => op.name === this.selectedOperatorName(),
    ),
  );

  requireRightOperand = computed(
    () => this.selectedOperator()?.requireRightOperand,
  );
  availableOperators = computed(() => {
    if (!this.operatorType()) {
      return [];
    } else {
      switch (this.operatorType()) {
        case 'integer':
          if (this.isNullable()) {
            return AvailableNoneNullableNumericOperators;
          } else {
            return AvailableNumericOperators;
          }

        case 'boolean':
          return AvailableBooleanOperators;
        case 'string':
          return AvailableStringOperators;
        case 'date':
          return AvailableDateOperators;
        default:
          return [];
      }
    }
  });

  //#region Properties
  get operatorControl(): UntypedFormControl | null {
    return this.conditionForm?.get('operator') as UntypedFormControl;
  }
  get rightOperandControl(): UntypedFormControl | null {
    return this.conditionForm?.get('rightOperand') as UntypedFormControl;
  }

  //#endregion
  constructor() {
    let previousOperatorType: OperatorType | null | undefined = null;

    effect(() => {
      const newOperatorType = this.operatorType();

      // empty operator and right operand when operatorType changes
      if (previousOperatorType && newOperatorType !== previousOperatorType) {
        this.operatorControl?.setValue(null);
        this.rightOperandControl?.setValue(null);
      }
      previousOperatorType = newOperatorType;
    });
  }

  ngOnInit(): void {
    this.conditionForm = this._controlContainer.control as UntypedFormGroup;

    this.operatorControl?.valueChanges
      .pipe(
        startWith(this.operatorControl.value),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe((newOperator: string | null | undefined) => {
        this.selectedOperatorName.set(newOperator || null);
      });

    //
  }
}
